import { afterNextRender, Component, computed, inject, OnDestroy, signal, PLATFORM_ID, Inject, effect } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { SignalsStoreService } from '../signals-store.service';
import { RouterModule } from '@angular/router';
import { GlobalMessageService } from './global-message.service';
import { map, tap } from 'rxjs';

const GLOBAL_MESSAGE_HEIGHT = '40px';

@Component({
  selector: 'app-global-message',
  imports: [
    RouterModule
  ],
  templateUrl: './global-message.component.html',
  styleUrl: './global-message.component.scss'
})
export class GlobalMessageComponent implements OnDestroy {

  //#region Services

  #signalsStorageService = inject(SignalsStoreService);
  #signalStoreService = inject(SignalsStoreService);
  #globalMessageService = inject(GlobalMessageService);
  #document = inject(DOCUMENT);

  //#endregion

  //#region Properties

  messages = computed(() => this.#signalsStorageService.globalMessages());
  currentIndex = signal(0);
  isPaused = signal(false);

  #rotationInterval: any = null;

  //#endregion

  //#region Constructors

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    // Update CSS variable when messages change
    effect(() => {
      const hasMessages = this.messages().length > 0;
      if (isPlatformBrowser(this.platformId)) {
        this.#document.documentElement.style.setProperty(
          '--global-message-offset',
          hasMessages ? GLOBAL_MESSAGE_HEIGHT : '0px'
        );
      }
    });

    afterNextRender(() => {
      this.#getGlobalMessages();
    })
  }

  ngOnDestroy(): void {
    this.#clearRotation();
  }

  //#endregion

  //#region Methods

  #startRotation(messageCount: number) {
    if (messageCount <= 1 || !isPlatformBrowser(this.platformId)) return;

    this.#clearRotation();
    this.#rotationInterval = setInterval(() => {
      if (!this.isPaused()) {
        this.currentIndex.update(current => (current + 1) % messageCount);
      }
    }, 5000);
  }

  #clearRotation() {
    if (this.#rotationInterval) {
      clearInterval(this.#rotationInterval);
      this.#rotationInterval = null;
    }
  }

  onMouseEnter() {
    this.isPaused.set(true);
  }

  onMouseLeave() {
    this.isPaused.set(false);
  }

  #getGlobalMessages() {

    this.#signalStoreService.globalMessages.set([]);

    this.#globalMessageService
      .getGlobalMessage()
      .pipe(
        map(res => {
          return (res ?? []).filter(x => this.#signalStoreService.hasSession() ? x.visibleWithSesion : x.visibleWithoutSesion);
        }),
        tap(res => {
          this.#signalStoreService.globalMessages.set(res);
          // Start rotation immediately after setting messages to prevent CLS
          if (res.length) this.#startRotation(res.length);
        })
      )
      .subscribe();
  }

  //#endregion
}
